/**
 * Cross-tab session sync.
 *
 * Tabs of one browser share a single localStorage session. When another tab rotates the
 * tokens this tab adopts them (the stale refresh token must never be sent again: the backend
 * would revoke the whole token family); when another tab logs out, clears the storage or signs
 * in as a different user, this tab ends its session. Nothing here writes localStorage, so no
 * storage event is raised back in the other tab.
 */

import type { Dispatch } from '@reduxjs/toolkit';
import type { RootState } from '../../../App/store';
import {
  sessionEndedElsewhere,
  tokensAdopted,
  type TokensAdoptedPayload,
} from '../slices/authSlice';
import type { UserInfo } from '../types/auth.types';

const AUTH_STORAGE_KEYS: readonly string[] = [
  'authToken',
  'authRefreshToken',
  'authUser',
  'authTokenExpiresAt',
  'authPasswordChangeRequired',
];
// One login or refresh writes five keys and a logout removes five: decide once the burst ends
const DEBOUNCE_MS = 75;

export interface AuthSyncStore {
  dispatch: Dispatch;
  getState: () => unknown;
}

/**
 * Reads the complete session persisted by login or refresh, or null when it is absent,
 * incomplete or unreadable (storage may throw). Same validation as the reader in
 * refreshSession, which is not exported.
 */
const readStoredSession = (): TokensAdoptedPayload | null => {
  const accessToken = localStorage.getItem('authToken');
  const refreshToken = localStorage.getItem('authRefreshToken');
  const userJson = localStorage.getItem('authUser');
  const expiresAtRaw = localStorage.getItem('authTokenExpiresAt');
  const tokenExpiresAt = Number(expiresAtRaw);
  const hasExpiry = !!expiresAtRaw && Number.isFinite(tokenExpiresAt) && tokenExpiresAt > 0;
  if (!accessToken || !refreshToken || !userJson || !hasExpiry) {
    return null;
  }
  const user = JSON.parse(userJson) as UserInfo | null;
  if (!user || typeof user.id !== 'number') {
    return null;
  }
  return {
    accessToken,
    refreshToken,
    user,
    tokenExpiresAt,
    passwordChangeRequired: localStorage.getItem('authPasswordChangeRequired') === 'true',
  };
};

/**
 * Compares this tab with the full persisted state and acts once. Anything incomplete or
 * unreadable is a write in progress or noise: do nothing and wait for the next event.
 */
const reconcile = (store: AuthSyncStore): void => {
  const auth = (store.getState() as RootState).auth;
  if (!auth.isAuthenticated) {
    return; // never resurrect a logged-out session
  }

  try {
    if (!localStorage.getItem('authRefreshToken')) {
      // A session that never had a refresh token must survive unrelated writes to the keys
      if (!localStorage.getItem('authToken') || auth.refreshToken) {
        store.dispatch(sessionEndedElsewhere());
      }
      return;
    }
    const stored = readStoredSession();
    if (!stored) {
      return;
    }
    if (stored.user.id !== auth.user?.id) {
      // Another user signed in: end this tab, never copy their tokens
      store.dispatch(sessionEndedElsewhere());
      return;
    }
    if (
      stored.refreshToken !== auth.refreshToken ||
      stored.tokenExpiresAt !== auth.tokenExpiresAt
    ) {
      store.dispatch(tokensAdopted(stored));
    }
  } catch {
    // Storage unavailable or invalid JSON: keep the current state
  }
};

/**
 * Listens for auth changes made by other tabs. Returns the cleanup function.
 */
export const startAuthStorageSync = (store: AuthSyncStore): (() => void) => {
  let timer: ReturnType<typeof setTimeout> | null = null;

  const onStorage = (event: StorageEvent): void => {
    // key === null: storage.clear()
    if (event.key !== null && !AUTH_STORAGE_KEYS.includes(event.key)) {
      return;
    }
    if (timer) {
      clearTimeout(timer);
    }
    timer = setTimeout(() => reconcile(store), DEBOUNCE_MS);
  };

  window.addEventListener('storage', onStorage);
  return () => {
    window.removeEventListener('storage', onStorage);
    if (timer) {
      clearTimeout(timer);
    }
  };
};
